import React from 'react';
import type { DateSelectArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import Calendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles/createTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import LoaderIcon from 'src/components/LoaderImg/loader.gif';
import { CalendarContainer } from 'src/components/calendar-container';
import { CalendarEventDialog } from 'src/components/calendar-event-dialog';
import { Seo } from 'src/components/seo';
import { useDialog } from 'src/hooks/use-dialog';
import { usePageView } from 'src/hooks/use-page-view';
import {
  getMyAvailabilityAction,
  resetPtoRequest,
  resetRemovePtoRequest,
} from 'src/redux/actions/myAvailabilityAction';
import type { CalendarView } from 'src/types/calendar';
import './CalenderStyle/custom-calendar-styles.css';
import EditMyAvailability from './EditMyAvailability/edit-my-availability';
import { EventContentComponent } from './My-event-calender';
import { CalendarToolbar } from './calendar-ttol-bar';
import useCurrentUser from 'src/hooks/use-current-user';

interface CreateDialogData {
  range?: {
    start: number;
    end: number;
  };
}

interface UpdateDialogData {
  eventId?: string;
}


type Props = {
  selectedTherapist: any;
};

const getDateInLocalIsoFormat = (date: any) => {
  const dateNewDate = new Date(date);
  return `${moment(dateNewDate).format('YYYY-MM-DD')}T00:00:00.000Z`;
};

const Page = (props: Props) => {
  const { selectedTherapist } = props;

  const dispatch = useDispatch();

  const calendarRef = useRef<Calendar | null>(null);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [date, setDate] = useState<Date>(new Date());

  const [view, setView] = useState<CalendarView>('dayGridMonth');
  const createDialog = useDialog<CreateDialogData>();
  const updateDialog = useDialog<UpdateDialogData>();

  const isIpadPortrait = useMediaQuery('(max-width:768px) and (max-height:1024px)');
  const isIpadLandscape = useMediaQuery('(max-width:1024px) and (max-height:768px)');

  const [providerId] = useCurrentUser(['providerId']);
  const getMyAvailability = useSelector((state: any) => state.getMyAvailabilityReducer);
  const isPtoRequested = useSelector((state: any) => state.createPtoRequestReducer);
  const removePtoReducer = useSelector((state: any) => state.removePtoRequestReducer);

  usePageView();

  const handleScreenResize = useCallback((): void => {
    const calendarEl = calendarRef.current;

    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      const newView = 'dayGridMonth';

      calendarApi.changeView(newView);
      setView(newView);
    }
  }, [calendarRef, mdUp]);

  useEffect(
    () => {
      handleScreenResize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mdUp]
  );

  const handleViewChange = useCallback((view: CalendarView): void => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.changeView(view);

      setView(view);
    }
  }, []);

  const handleDateToday = useCallback((): void => {
    const calendarEl = calendarRef.current;

    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  }, []);

  const handleDatePrev = useCallback((): void => {
    const calendarEl = calendarRef.current;

    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  }, []);

  const handleDateNext = useCallback((): void => {
    const calendarEl = calendarRef.current;

    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  }, []);

  const [editMyAvaliability, setEditMyAvaliability] = useState(false);

  const handleAddClick = useCallback((): void => {
    setEditMyAvaliability(true);
  }, [createDialog]);

  const closeEditMyAvailability = () => {
    setEditMyAvaliability(false);
  };

  const handleRangeSelect = useCallback(
    (arg: DateSelectArg): void => {
      const calendarEl = calendarRef.current;

      if (calendarEl) {
        const calendarApi = calendarEl.getApi();

        calendarApi.unselect();
      }

      createDialog.handleOpen({
        range: {
          start: arg.start.getTime(),
          end: arg.end.getTime(),
        },
      });
    },
    [createDialog]
  );

  const [getWeekRange, setGetWeekRange] = useState({
    start: '',
    end: '',
  });

  const handleEventResize = useCallback(async (arg: EventResizeDoneArg): Promise<void> => {}, []);

  const [events, setEvents] = useState([]);
  const [errorInGet, setErrorInGet] = useState({
    isError: false,
    message: '',
  });

  useEffect(() => {
    if (selectedTherapist && editMyAvaliability) {
      setEditMyAvaliability(false);
    }
  }, [selectedTherapist]);

  useEffect(() => {
    if (!getMyAvailability.isGetAvailability && getMyAvailability?.response === null) return;
    if (!getMyAvailability.isGetAvailability && getMyAvailability?.response !== null) {
      setErrorInGet((prev) => ({
        ...prev,
        isError: true,
        message: getMyAvailability?.response?.message,
      }));
    }
    if (getMyAvailability?.data?.t1?.length !== 0) {
      const getMyAvailabilityEvent = getMyAvailability?.data?.t1?.map((dayEvents: any) => {
        function setTimeTo0630(inputDate: any) {
          const [datePart] = inputDate.split('T');
          const modifiedDate = `${datePart}T06:30:00`;
          return modifiedDate;
        }
        const utcDate = moment(dayEvents?.currentDate)?.utc();
        return {
          date: dayEvents?.currentDate,
          uniqueDate: moment(utcDate)?.format('MM/DD/YYYY'),
          start: setTimeTo0630(dayEvents?.currentDate),
          isBlocked: dayEvents?.isBlocked,
          end: dayEvents?.currentDate,
          extendedProps: {
            hours: dayEvents?.hoursAvailable,
            start: dayEvents?.startTime?.substring(0, 5),
            ptoRequest: dayEvents?.ptoResponses,
          },
        };
      });
      setEvents(getMyAvailabilityEvent);
    }
  }, [getMyAvailability]);

  function getWeekEndDate(date: any) {
    const dayOfWeek = date.getDay();
    const daysUntilEndOfWeek = 7 - dayOfWeek;

    const endDate = new Date(date);
    endDate.setDate(date.getDate() + daysUntilEndOfWeek);

    return endDate;
  }

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const start = calendarApi.view.currentStart;
      start.setDate(start.getDate());
      const end: any =
        view === 'timeGridWeek'
          ? getWeekEndDate(start)
          : new Date(start.getFullYear(), start.getMonth() + 1, 0);
      const startMonthDate: any = new Date(start.getFullYear(), start.getMonth(), 1);
      const isTodayDate = new Date(date).toLocaleDateString() === new Date().toLocaleDateString();
      view === 'dayGridMonth' &&
        !isTodayDate &&
        setGetWeekRange({
          start: startMonthDate,
          end: end,
        });
      view === 'dayGridMonth' && !isTodayDate && setDate(startMonthDate);
      view === 'timeGridWeek' &&
        setGetWeekRange((prev: any) => ({
          ...prev,
          start: calendarApi.view.currentStart,
          end: getWeekEndDate(calendarApi.view.currentStart),
        }));

      dispatch(
        getMyAvailabilityAction({
          startDate: getDateInLocalIsoFormat(start),
          endDate: getDateInLocalIsoFormat(end),
          providerId: selectedTherapist
            ? selectedTherapist.uuid
            : providerId,
        })
      );
      dispatch(resetPtoRequest());
      dispatch(resetRemovePtoRequest());
    }
  }, [calendarRef, date, editMyAvaliability, view, selectedTherapist]);

  useEffect(() => {
    if (isPtoRequested?.isCreated || removePtoReducer?.isRemoved) {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        const start = calendarApi.view.currentStart;
        start.setDate(start.getDate());

        const end =
          view === 'timeGridWeek'
            ? getWeekEndDate(start)
            : new Date(start.getFullYear(), start.getMonth() + 1, 0);

        view === 'timeGridWeek' &&
          setGetWeekRange((prev: any) => ({
            ...prev,
            start: calendarApi.view.currentStart,
            end: getWeekEndDate(calendarApi.view.currentStart),
          }));
        dispatch(
          getMyAvailabilityAction({
            startDate: getDateInLocalIsoFormat(start),
            endDate: getDateInLocalIsoFormat(end),
            providerId: providerId,
          })
        );
        dispatch(resetPtoRequest());
        dispatch(resetRemovePtoRequest());
      }
    }
  }, [isPtoRequested, removePtoReducer]);

  const memoizedProps = useMemo(() => {
    return {
      closeAvialibility: closeEditMyAvailability,
      selectedTherapist: selectedTherapist,
      changedDate: date,
    };
  }, [closeEditMyAvailability, date]);

  const dayCellClassNames = (arg: any) => {
    const newDate = moment(arg?.date)?.format('MM/DD/YYYY');
    if (events?.length > 0) {
      const matchingEvent = events?.find(
        (event: any) => event.isBlocked && newDate === event.uniqueDate
      );
      if (matchingEvent) {
        return 'custom-date-class';
      }
      return '';
    }
  };

  const isCurrentTherapist =
    !selectedTherapist ||
    providerId === selectedTherapist?.uuid ||
    isIpadPortrait ||
    isIpadLandscape
      ? true
      : false;

  return (
    <>
      <Seo title="Dashboard: Calendar" />
      {!editMyAvaliability ? (
        <>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: 3,
            }}
          >
            <Container maxWidth="xl">
              <Stack>
                <Grid xs={true}>
                  <CalendarToolbar
                    selectedTherapist={selectedTherapist}
                    errorInGet={errorInGet}
                    date={date}
                    onAddClick={handleAddClick}
                    onDateNext={handleDateNext}
                    onDatePrev={handleDatePrev}
                    onDateToday={handleDateToday}
                    onViewChange={handleViewChange}
                    view={view}
                    getWeekRange={getWeekRange}
                  />
                </Grid>

                <Card sx={{ mt: 3 }}>
                  {/* @ts-ignore */}
                  <CalendarContainer isMediumSize={isIpadPortrait || isIpadLandscape}>
                    {getMyAvailability.loading ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          height: '45vh',
                        }}
                      >
                        <img
                          src={LoaderIcon}
                          alt="loader.."
                        />
                      </div>
                    ) : (
                      <Calendar
                        allDayMaintainDuration
                        dayMaxEventRows={3}
                        eventDisplay="block"
                        eventResizableFromStart
                        eventResize={handleEventResize}
                        slotLabelContent={() => ''}
                        allDaySlot={false}
                        slotMinTime="00:00:00"
                        events={events}
                        headerToolbar={false}
                        initialDate={date}
                        selectable={false}
                        // @ts-ignore
                        dayCellClassNames={dayCellClassNames}
                        initialView={view}
                        contentHeight={950}
                        plugins={[
                          dayGridPlugin,
                          interactionPlugin,
                          listPlugin,
                          timeGridPlugin,
                          timelinePlugin,
                        ]}
                        ref={calendarRef}
                        rerenderDelay={10}
                        select={handleRangeSelect}
                        weekends
                        eventContent={(props) => <EventContentComponent {...props} />}
                        firstDay={1}
                      />
                    )}
                  </CalendarContainer>
                </Card>
              </Stack>
            </Container>
          </Box>
          <CalendarEventDialog
            action="create"
            onAddComplete={createDialog.handleClose}
            onClose={createDialog.handleClose}
            open={createDialog.open}
            range={createDialog.data?.range}
          />
          <CalendarEventDialog
            action="update"
            onClose={updateDialog.handleClose}
            onDeleteComplete={updateDialog.handleClose}
            onEditComplete={updateDialog.handleClose}
            open={updateDialog.open}
          />
        </>
      ) : (
        <EditMyAvailability {...memoizedProps} />
      )}
    </>
  );
};

export default Page;
